/**
 * Minimal ANSI SGR parser for tool output. Handles 16/256/truecolor fg+bg, bold, dim,
 * italic, underline, inverse and resets; strips every other escape (cursor movement, OSC
 * hyperlinks, erase) and resolves carriage-return progress redraws per line.
 */
const NAMES = ['black', 'red', 'green', 'yellow', 'blue', 'magenta', 'cyan', 'white'];
/** Colour value: a `--vk-ansi-*` variable for the 16-colour palette, else an rgb() string. */
function palette(n) {
    if (n < 8)
        return `var(--vk-ansi-${NAMES[n]})`;
    if (n < 16)
        return `var(--vk-ansi-bright-${NAMES[n - 8]})`;
    if (n < 232) {
        const i = n - 16;
        const lv = [0, 95, 135, 175, 215, 255];
        return `rgb(${lv[Math.floor(i / 36)]} ${lv[Math.floor(i / 6) % 6]} ${lv[i % 6]})`;
    }
    const g = 8 + (n - 232) * 10;
    return `rgb(${g} ${g} ${g})`;
}
function applySgr(style, codes) {
    let s = { ...style };
    for (let i = 0; i < codes.length; i++) {
        const c = codes[i] ?? 0;
        if (c === 0)
            s = {};
        else if (c === 1)
            s.bold = true;
        else if (c === 2)
            s.dim = true;
        else if (c === 3)
            s.italic = true;
        else if (c === 4)
            s.underline = true;
        else if (c === 7)
            s.inverse = true;
        else if (c === 22) {
            s.bold = false;
            s.dim = false;
        }
        else if (c === 23)
            s.italic = false;
        else if (c === 24)
            s.underline = false;
        else if (c === 27)
            s.inverse = false;
        else if (c >= 30 && c <= 37)
            s.fg = palette(c - 30);
        else if (c === 39)
            s.fg = undefined;
        else if (c >= 40 && c <= 47)
            s.bg = palette(c - 40);
        else if (c === 49)
            s.bg = undefined;
        else if (c >= 90 && c <= 97)
            s.fg = palette(c - 90 + 8);
        else if (c >= 100 && c <= 107)
            s.bg = palette(c - 100 + 8);
        else if (c === 38 || c === 48) {
            const mode = codes[i + 1];
            let color;
            if (mode === 5) {
                color = palette(codes[i + 2] ?? 0);
                i += 2;
            }
            else if (mode === 2) {
                color = `rgb(${codes[i + 2] ?? 0} ${codes[i + 3] ?? 0} ${codes[i + 4] ?? 0})`;
                i += 4;
            }
            if (c === 38)
                s.fg = color;
            else
                s.bg = color;
        }
    }
    return s;
}
/** Resolve `\r` redraws: within a line, only the text after the last bare CR survives. */
export function resolveCarriageReturns(text) {
    if (!text.includes('\r'))
        return text;
    return text
        .replace(/\r\n/g, '\n')
        .split('\n')
        .map((line) => {
        const k = line.lastIndexOf('\r');
        return k < 0 ? line : line.slice(k + 1);
    })
        .join('\n');
}
const ESC = 
// biome-ignore lint/suspicious/noControlCharactersInRegex: matching terminal escapes is the point
/\x1b\[([0-9;?]*)([A-Za-z])|\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)|\x1b[()][0-9A-B]|\x1b[=>78]/g;
export function parseAnsi(input) {
    const text = resolveCarriageReturns(input);
    const spans = [];
    let style = {};
    let last = 0;
    ESC.lastIndex = 0;
    for (let m = ESC.exec(text); m; m = ESC.exec(text)) {
        if (m.index > last)
            spans.push({ text: text.slice(last, m.index), style });
        if (m[2] === 'm') {
            const codes = (m[1] ?? '')
                .split(';')
                .filter((x) => x !== '')
                .map(Number);
            style = applySgr(style, codes.length ? codes : [0]);
        }
        last = ESC.lastIndex;
    }
    if (last < text.length)
        spans.push({ text: text.slice(last), style });
    return spans.filter((s) => s.text.length > 0);
}
export function stripAnsi(input) {
    return parseAnsi(input)
        .map((s) => s.text)
        .join('');
}
/** Split spans into lines (each line an array of spans) for line-numbered rendering. */
export function ansiLines(input) {
    const lines = [[]];
    for (const span of parseAnsi(input)) {
        const parts = span.text.split('\n');
        parts.forEach((p, i) => {
            if (i > 0)
                lines.push([]);
            if (p)
                lines[lines.length - 1].push({ text: p, style: span.style });
        });
    }
    if (lines.length > 1 && lines[lines.length - 1].length === 0)
        lines.pop();
    return lines;
}
//# sourceMappingURL=ansi.js.map